import type { FC, ReactNode } from "react";
import type { DayKey } from "@/lib/days";
import type {
  WeekData,
  WidgetConfig,
  WidgetConfigBase,
  WidgetWeekEntry,
} from "@/lib/schema";

export type WidgetValidationResult =
  | { ok: true }
  | { ok: false; errors: Record<string, string> };

export interface WidgetSettingsEditorProps<TConfig extends WidgetConfigBase> {
  config: TConfig;
  onChange: (patch: Partial<TConfig>) => void;
  errors?: Record<string, string>;
  allWidgets?: WidgetConfig[];
  scoringField?: ReactNode;
  // Lets an editor lock a choice that is free to make while creating but
  // destructive to change later. Absent in the settings preview.
  mode?: "create" | "edit";
}

export interface WidgetDayViewProps<TConfig extends WidgetConfigBase> {
  config: TConfig;
  data: unknown;
  weekData: unknown;
  dayKey: DayKey;
  onChangeDay: (next: unknown) => void;
  onChangeWeek: (next: unknown) => void;
  readOnly?: boolean;
  weekId?: string;
  week?: WeekData;
}

export interface WidgetWeekViewProps<TConfig extends WidgetConfigBase> {
  config: TConfig;
  weekData: unknown;
  allWidgets: WidgetWeekEntry[];
  previousWeek: WeekData | null;
  readOnly?: boolean;
  // The viewed week itself; absent in settings preview (falls back to
  // the current ISO week).
  week?: WeekData;
}

interface ScoreRuntime {
  week: unknown;
  days: Record<DayKey, unknown>;
}

/**
 * One separately-trackable half of a kind's score (docs/adr/0010) — for
 * workday-log, the hours and the productivity periods. Each carries its own
 * week scoring and its own week goal so a weekly-point-summary row can follow
 * one without the other.
 */
export interface ScorablePart<TConfig extends WidgetConfigBase> {
  /** Stable across releases: it is stored inside sealed week snapshots. */
  key: string;
  /** Shown after the instance's label — "Arbeidsdag · Arbeidstid". */
  label: string;
  scoreWeek: (config: TConfig, runtime: ScoreRuntime) => number;
  weekGoal: (config: TConfig) => number;
  /**
   * Whether this config has the part at all — workout-log's Planlegging
   * exists only while planning is switched on. Absent means always. Read
   * through `partsOf`, never directly.
   */
  appliesTo?: (config: TConfig) => boolean;
}

interface BaseWidgetModule<TConfig extends WidgetConfigBase> {
  kind: TConfig["kind"];
  label: string;
  defaults: (existing: WidgetConfig[]) => TConfig;
  validate: (config: TConfig) => WidgetValidationResult;
  /**
   * Settles structure the editor does not let you author, at the moment the
   * config is saved — meal-log's Betingelsesrekkefølge (docs/adr/0021) is the
   * only such structure today.
   *
   * Deliberately not applied while editing: the sort keys are themselves
   * inputs, so a live sort would slide a row out from under the cursor
   * mid-keystroke. Settling on save is what makes reopening the settings show
   * the order every other view renders.
   */
  normalize?: (config: TConfig) => TConfig;
  SettingsEditor: FC<WidgetSettingsEditorProps<TConfig>>;
  /**
   * What a week loses if this instance is dropped from it — "4 netter",
   * "14 måltider" — or null when the entry holds nothing worth naming.
   *
   * Read only by the update modal (docs/adr/0014), to state the cost of a
   * removal before it happens, since applying the mal takes the runtime with
   * the widget and there is no undo. Kinds that store nothing in the week row
   * simply omit it: week-scope kinds, because nothing is logged into a summary
   * or a graph, and body-measurement-log, whose entries live globally and
   * survive the widget entirely (docs/adr/0002).
   */
  describeWeekData?: (entry: WidgetWeekEntry) => string | null;
}

interface ScoredAddon<TConfig extends WidgetConfigBase> {
  scoring: "points";
  // `weekData` is the instance's week-scoped runtime. Kinds that keep all
  // their state per day ignore it; recurring-tasks on weekly reset needs it,
  // because a finished weekly set's points live on one day named there
  // (CONTEXT.md: "Award day") rather than in any day's own data.
  scoreDay?: (
    config: TConfig,
    dayData: unknown,
    dayKey: DayKey,
    weekData: unknown,
  ) => number;
  scoreWeek: (config: TConfig, runtime: ScoreRuntime) => number;
  maxScoreWeek: (config: TConfig) => number;
  /**
   * Named halves of this kind's score, each separately referenceable from a
   * weekly-point-summary row (docs/adr/0010). Declaring parts carries two
   * obligations:
   *
   * - The kind is offered to summary rows **as its parts** and never as a
   *   whole, so no row can count a half and its container twice. A row that
   *   wants the total lists both parts.
   * - The kind owns its week-goal inputs: WidgetSettingsModal renders no
   *   shared Ukesmål field for it, and its editor must keep `weekGoal` equal
   *   to the sum of the parts' goals — DayStrip's week tile and whole-widget
   *   references sealed into older snapshots both still read it.
   */
  parts?: ScorablePart<TConfig>[];
}

/**
 * The parts `config` actually has, or none when fewer than two apply — a
 * lone part is the whole widget, so the instance is then offered and goaled
 * as one (docs/adr/0010).
 */
export function partsOf<TConfig extends WidgetConfigBase>(
  module: WidgetModule<TConfig>,
  config: TConfig,
): ScorablePart<TConfig>[] {
  if (module.scoring !== "points" || !module.parts) return [];
  const applying = module.parts.filter(
    (part) => part.appliesTo?.(config) ?? true,
  );
  return applying.length >= 2 ? applying : [];
}

interface UnscoredAddon {
  scoring: "none";
}

interface DayWidgetModuleBase<TConfig extends WidgetConfigBase>
  extends BaseWidgetModule<TConfig> {
  scope: "day";
  DayView: FC<WidgetDayViewProps<TConfig>>;
  isVisibleForDay?: (config: TConfig, dayKey: DayKey) => boolean;
}

interface WeekWidgetModuleBase<TConfig extends WidgetConfigBase>
  extends BaseWidgetModule<TConfig> {
  scope: "week";
  WeekView: FC<WidgetWeekViewProps<TConfig>>;
}

export type DayWidgetModule<TConfig extends WidgetConfigBase> =
  | (DayWidgetModuleBase<TConfig> & ScoredAddon<TConfig>)
  | (DayWidgetModuleBase<TConfig> & UnscoredAddon);

export type WeekWidgetModule<TConfig extends WidgetConfigBase> =
  | (WeekWidgetModuleBase<TConfig> & ScoredAddon<TConfig>)
  | (WeekWidgetModuleBase<TConfig> & UnscoredAddon);

export type WidgetModule<TConfig extends WidgetConfigBase> =
  | DayWidgetModule<TConfig>
  | WeekWidgetModule<TConfig>;
